import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import Header from 'components/headers/withGreenBack'
import DotsLoading from 'components/loadings/dotsLoading'
import { toman } from 'utilities/helper/format'
import { claimCashboxPrize } from 'services/cashbox'
import { getBankCards } from 'services/bankManagements'
import {
    Card,
    Notice,
    PrimaryButton,
    apiMessage,
    inputClass,
} from 'modules/cashbox/ui'
import { CASHBOX_KEY, useCashbox } from 'modules/cashbox/hooks'

/**
 * دریافت وام — the winner collecting their prize.
 *
 * Available only once the manager has paid the winners out (the draw itself
 * moves no money on current app versions), which is what puts a cashout on the
 * membership. `have_cashout` on the caller's membership is that signal — the
 * backend refuses the claim without it.
 *
 * `wlt` credits the Hamyan wallet immediately; `mba` starts a bank transfer
 * and needs one of the member's own accounts.
 */
export default function CashboxClaimPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { cashbox, isLoading } = useCashbox(id)

    const [destination, setDestination] = useState<'wlt' | 'mba'>('wlt')
    const [bankAccountId, setBankAccountId] = useState<number | ''>('')

    const { data: cards, isLoading: cardsLoading } = useQuery(
        ['bank-cards'],
        () => getBankCards()
    )

    const accounts = ((cards as any)?.results ?? []) as Array<any>

    const claim = useMutation({
        mutationFn: () =>
            claimCashboxPrize({
                id: id!,
                destination,
                ...(destination === 'mba'
                    ? { bank_account_id: Number(bankAccountId) }
                    : {}),
            }),
        onSuccess: () => {
            // The member's end of the loan. `destination` is the interesting
            // half: کیف پول lands instantly, حساب بانکی goes through Paya and is
            // where "پولم کجاست" questions come from.
            posthog.capture('cashbox_prize_claimed', {
                cashbox_id: id,
                destination,
                amount: cashbox?.membership?.cashout_amount,
            })
            toast.success(
                destination === 'wlt'
                    ? 'مبلغ به کیف پول شما اضافه شد'
                    : 'درخواست انتقال به حساب ثبت شد'
            )
            queryClient.invalidateQueries(CASHBOX_KEY(id))
            queryClient.invalidateQueries(['get-boxes'])
            navigate(`/cashbox/${id}`)
        },
        onError: (error: any) => {
            posthog.capture('cashbox_prize_claim_failed', {
                cashbox_id: id,
                destination,
                amount: cashbox?.membership?.cashout_amount,
                status: error?.response?.status,
            })
            toast.error(apiMessage(error))
        },
    })

    if (isLoading || cardsLoading) return <DotsLoading />

    const membership = cashbox?.membership

    if (!membership?.have_cashout)
        return (
            <div className="bg-white min-h-screen">
                <Header title="دریافت وام" />
                <div className="p-4">
                    <Notice tone="info">
                        در حال حاضر وامی برای دریافت ندارید. اگر در قرعه‌کشی
                        برنده شده‌اید، تا زمانی که مدیر صندوق وام را پرداخت
                        نکند اینجا چیزی نمایش داده نمی‌شود.
                    </Notice>
                </div>
            </div>
        )

    const canSubmit =
        destination === 'wlt' || (destination === 'mba' && bankAccountId !== '')

    return (
        <div className="bg-[#FAFAFA] min-h-screen pb-8">
            <div className="bg-white">
                <Header title="دریافت وام" />
            </div>

            <div className="px-4 pt-3">
                <Card className="mb-3">
                    <div className="flex justify-between items-baseline">
                        <span className="text-sm text-text-secondary">
                            مبلغ قابل دریافت
                        </span>
                        <span className="text-base font-bold text-black">
                            {toman(membership.cashout_amount)}
                        </span>
                    </div>
                </Card>

                <p className="text-sm font-bold text-black mb-2">واریز به</p>
                <div className="flex gap-2 mb-3">
                    {(
                        [
                            { value: 'wlt', label: 'کیف پول همیان' },
                            { value: 'mba', label: 'حساب بانکی' },
                        ] as Array<{ value: 'wlt' | 'mba'; label: string }>
                    ).map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            onClick={() => setDestination(option.value)}
                            className={`flex-1 h-11 rounded-[10px] border text-sm ${
                                destination === option.value
                                    ? 'border-green-1 bg-[#E6F3DD] text-green-1 font-bold'
                                    : 'border-divider bg-white text-text-body'
                            }`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>

                {destination === 'mba' && (
                    <div className="mb-3">
                        {accounts.length === 0 ? (
                            <Notice tone="warn">
                                هنوز حساب بانکی ثبت نکرده‌اید. از بخش
                                «حساب‌های بانکی» یک حساب اضافه کنید.
                            </Notice>
                        ) : (
                            <select
                                value={bankAccountId}
                                onChange={(event) =>
                                    setBankAccountId(Number(event.target.value))
                                }
                                className={inputClass}
                            >
                                <option value="">انتخاب حساب…</option>
                                {accounts.map((account) => (
                                    <option key={account.id} value={account.id}>
                                        {account.bank_name} ·{' '}
                                        {account.card_number ??
                                            account.sheba_number ??
                                            account.owner_name}
                                    </option>
                                ))}
                            </select>
                        )}
                        <p className="text-xs text-text-secondary mt-2">
                            انتقال به حساب بانکی ممکن است تا چند ساعت زمان
                            ببرد؛ واریز به کیف پول فوری است.
                        </p>
                    </div>
                )}

                <div className="mt-4">
                    <PrimaryButton
                        disabled={!canSubmit || claim.isLoading}
                        onClick={() => claim.mutate()}
                    >
                        {claim.isLoading ? 'در حال ثبت…' : 'دریافت'}
                    </PrimaryButton>
                </div>
            </div>
        </div>
    )
}
